/**
 * Shared upload / early-pipeline stall detection (no Firestore % within grace window).
 */

import { resolveCaptureListDisplayPercent } from './listDisplay.js';
import { resolveCanonicalPipelineStage } from './stageMapping.js';
import { readCaptureFrameFields } from '../../utils/pipeline/pipelineFrameFields.js';

/**
 * True when a capture is stuck before meaningful pipeline progress (matches capture list stall).
 *
 * @param {object} params
 */
export function isCaptureUploadStalled({
  captureMetadata = null,
  captureId = null,
  isFailed = false,
  pollApplies = false,
  polledStatus = null,
  pollPercent = null,
  pollStageIndex = null,
  pollStageTitle = null,
  polledRunpodJobStatus = null,
  activeCaptureId = null,
  uploadPhase = null,
  uploadPercent = null,
  pipelinePercent = null,
  graceMs = 2 * 60 * 1000,
} = {}) {
  if (!captureMetadata || isFailed) return false;

  const frameFields = readCaptureFrameFields(captureMetadata.processing || {});
  const metadataStage = resolveCanonicalPipelineStage({
    isClientUploading: false,
    processingStatus: captureMetadata.processing?.status,
    pipelineStage: captureMetadata.processing?.stage,
    progressPercent: captureMetadata.progress?.percent,
    progressMessage: captureMetadata.progress?.message,
    pollPercent: pollApplies ? pollPercent : null,
    pollStageIndex,
    pollStageTitle,
    runpodJobStatus: polledRunpodJobStatus,
    framesExtracted: frameFields.framesExtracted,
  });
  const pipelinePastUpload = !metadataStage.isQueueing && metadataStage.segmentIndex >= 1;
  const hasTerminalPipelineSignal =
    captureMetadata.processing?.status === 'failed'
    || (pollApplies && (polledStatus === 'failed' || polledStatus === 'cancelled'))
    || !!captureMetadata.processing?.failedAt
    || !!captureMetadata.processing?.failureDetail;
  if (pipelinePastUpload || hasTerminalPipelineSignal) return false;
  if (
    pollApplies
    && polledStatus
    && polledStatus !== 'failed'
    && polledStatus !== 'cancelled'
  ) {
    return false;
  }

  const disp = resolveCaptureListDisplayPercent(
    {
      id: captureId,
      processing: captureMetadata.processing,
      progress: captureMetadata.progress,
      createdAt: captureMetadata.createdAt,
      updatedAt: captureMetadata.updatedAt,
    },
    {
      activeCaptureId,
      uploadPhase,
      uploadPercent,
      pipelinePercent,
      progressPercent: uploadPhase === 'uploading' ? uploadPercent : pipelinePercent,
    },
    { graceMs },
  );
  return disp.stalled;
}

/**
 * Build per-segment bar states for ReconstructionPipeline (studio + Shopify themes).
 */
export function buildPipelineSegmentStates({
  segmentCount = 4,
  statusForPipeline,
  failedIndex,
  barActiveIndex,
  isFailedState,
  isQueueingUi,
  isCompleted,
  clientUploadInProgress,
  uploadStageComplete,
  frameExtractionBarComplete,
}) {
  const segments = [];
  for (let idx = 0; idx < segmentCount; idx += 1) {
    const isUploadStage = idx === 0;
    const isFrameExtractionStage = idx === 1;
    const isFailedStage = isFailedState && failedIndex >= 0 && idx === failedIndex;

    if (isCompleted) {
      segments.push({
        idx,
        isDone: true,
        isActive: false,
        isFailed: false,
        isUploadComplete: isUploadStage,
      });
      continue;
    }

    if (isFailedState && failedIndex >= 0) {
      segments.push({
        idx,
        isDone: idx < failedIndex,
        isActive: false,
        isFailed: isFailedStage,
        isUploadComplete: isUploadStage && idx < failedIndex && !clientUploadInProgress,
      });
      continue;
    }

    const uploadBarComplete = isUploadStage && !clientUploadInProgress && (
      isQueueingUi || uploadStageComplete || frameExtractionBarComplete
    );
    const frameBarComplete = isFrameExtractionStage && frameExtractionBarComplete;
    const isDone = uploadBarComplete
      || frameBarComplete
      || (failedIndex >= 0 ? idx < failedIndex : idx < barActiveIndex);
    const isActive = !isDone && (
      !isQueueingUi
      || (clientUploadInProgress && isUploadStage)
    )
      && failedIndex < 0
      && idx === barActiveIndex
      && barActiveIndex < segmentCount;

    segments.push({
      idx,
      isDone,
      isActive,
      isFailed: false,
      isUploadComplete: uploadBarComplete,
    });
  }
  return segments;
}
